import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Check, ChevronRight, GraduationCap } from "lucide-react";
import { toast } from "sonner";

const PROCESS_QUESTIONS = [
  { code: "CICLO_ATTIVO", q: "Emetti fatture ai tuoi clienti?" },
  { code: "CICLO_PASSIVO", q: "Ricevi fatture da fornitori?" },
  { code: "IMMOBILIZZAZIONI", q: "Hai beni aziendali (macchinari, attrezzature, immobili)?" },
  { code: "PERSONALE", q: "Hai dipendenti?" },
  { code: "FINANZIARIA", q: "Hai finanziamenti bancari?" },
  { code: "MAGAZZINO", q: "Gestisci un magazzino?" },
];

const STEPS = ["La tua azienda", "I tuoi rischi", "Come li gestisci", "Sintesi"];

export default function PmiGuide() {
  const { org } = useAuth();
  const [step, setStep] = useState(0);
  const [active, setActive] = useState({});
  const [risks, setRisks] = useState([]);
  const [present, setPresent] = useState({});
  const [controls, setControls] = useState({});

  useEffect(() => {
    api.get("/risks/catalog?pmi_only=true").then(({ data }) => setRisks(data));
  }, []);

  const toggleProcess = (code) => setActive((a) => ({ ...a, [code]: !a[code] }));

  const next = async () => {
    if (step === 0) {
      const selectedProcesses = Object.entries(active).filter(([, v]) => v).map(([k]) => k);
      setStep(1);
    } else if (step === 1) {
      setStep(2);
    } else if (step === 2) {
      // Create controls for each present risk
      try {
        const risksData = await api.get("/risks");
        const orgRisks = risksData.data;
        for (const [riskId, isPresent] of Object.entries(present)) {
          if (!isPresent) continue;
          const orgRisk = orgRisks.find((r) => r.catalog_risk_id === riskId);
          if (!orgRisk) continue;
          await api.put(`/risks/${orgRisk.id}`, { is_present: true, impact: "medio", probability: "medio" });
          const ctrlMode = controls[riskId];
          if (ctrlMode) {
            const desc = {
              manuale: "Verifica manuale mensile",
              software: "Controllo automatizzato gestionale",
              commercialista: "Controllo a cura del commercialista",
              nessuno: "Controllo da implementare",
            }[ctrlMode];
            await api.post("/controls", {
              org_risk_id: orgRisk.id, organization_id: org.id,
              description: desc, frequency: "mensile",
              is_preventive: ctrlMode !== "nessuno", is_automatic: ctrlMode === "software",
            });
          }
        }
        toast.success("Configurazione PMI salvata");
      } catch (e) { toast.error("Errore"); }
      setStep(3);
    }
  };

  const selectedProcesses = Object.entries(active).filter(([, v]) => v).map(([k]) => k);
  const filteredRisks = risks.filter((r) => selectedProcesses.includes(r.process_code));

  return (
    <div className="space-y-6 max-w-4xl" data-testid="pmi-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-tag">Regime semplificato</div>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 mt-1 flex items-center gap-2"><GraduationCap size={32} /> Guida TCF per PMI</h1>
        </div>
      </div>

      <div className="flex items-center gap-2">
        {STEPS.map((label, i) => (
          <div key={label} className="flex-1 flex items-center gap-2">
            <div className={`w-8 h-8 rounded-sm border flex items-center justify-center text-sm font-medium ${i <= step ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-300 text-slate-400"}`}>
              {i < step ? <Check size={14} /> : i + 1}
            </div>
            <div className={`text-xs ${i <= step ? "text-slate-900 font-medium" : "text-slate-400"}`}>{label}</div>
            {i < STEPS.length - 1 && <div className="flex-1 h-px bg-slate-200" />}
          </div>
        ))}
      </div>

      <Card className="card-flat p-8">
        {step === 0 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl font-semibold text-slate-900">Rispondi a queste semplici domande</h2>
            {PROCESS_QUESTIONS.map((p) => (
              <div key={p.code} className="flex items-center justify-between border-b border-slate-100 py-3">
                <div className="text-slate-800">{p.q}</div>
                <div className="flex gap-2">
                  <Button variant={active[p.code] ? "default" : "outline"} size="sm" onClick={() => setActive((a) => ({ ...a, [p.code]: true }))} className="rounded-sm" data-testid={`pmi-yes-${p.code}`}>Sì</Button>
                  <Button variant={active[p.code] === false ? "default" : "outline"} size="sm" onClick={() => setActive((a) => ({ ...a, [p.code]: false }))} className="rounded-sm">No</Button>
                </div>
              </div>
            ))}
          </div>
        )}
        {step === 1 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl font-semibold text-slate-900">I tuoi principali rischi</h2>
            <p className="text-sm text-slate-600">Per ogni rischio, indica se ti riguarda.</p>
            {filteredRisks.length === 0 && <div className="text-sm text-slate-500">Nessun rischio PMI corrispondente.</div>}
            {filteredRisks.map((r) => (
              <Card key={r.id} className="card-flat p-4">
                <div className="text-sm text-slate-800 mb-3">{r.description}</div>
                <div className="flex gap-2">
                  <Button variant={present[r.id] ? "default" : "outline"} size="sm" onClick={() => setPresent((s) => ({ ...s, [r.id]: true }))} className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white">Sì, ho questo rischio</Button>
                  <Button variant={present[r.id] === false ? "default" : "outline"} size="sm" onClick={() => setPresent((s) => ({ ...s, [r.id]: false }))} className="rounded-sm">No, non ho questo rischio</Button>
                </div>
              </Card>
            ))}
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl font-semibold text-slate-900">Come li gestisci?</h2>
            {filteredRisks.filter((r) => present[r.id]).map((r) => (
              <Card key={r.id} className="card-flat p-4">
                <div className="text-sm text-slate-800 mb-3">{r.description}</div>
                <div className="grid grid-cols-2 gap-2">
                  {[["manuale", "Verifico manualmente"], ["software", "Software automatico"], ["commercialista", "Commercialista"], ["nessuno", "Nessun controllo"]].map(([v, l]) => (
                    <Button key={v} variant={controls[r.id] === v ? "default" : "outline"} size="sm" onClick={() => setControls((s) => ({ ...s, [r.id]: v }))} className="rounded-sm">{l}</Button>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4">
            <h2 className="font-display text-xl font-semibold text-slate-900">Sintesi & prossimi passi</h2>
            <p className="text-sm text-slate-600">Il tuo TCF semplificato è ora configurato. Vai alla Dashboard per visualizzare il TCF Score iniziale.</p>
          </div>
        )}

        <div className="flex justify-end mt-8">
          {step < STEPS.length - 1 && <Button onClick={next} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="pmi-next">Continua <ChevronRight size={14} className="ml-1" /></Button>}
        </div>
      </Card>
    </div>
  );
}
